import { ImportOutlined } from "@ant-design/icons";
import { Button, Modal, Select } from "antd";
import React, { useMemo, useState } from "react";
import { useNotification } from "../../contexts/Notifications";
import {
  useCopyTasksAndComponentsMutation,
  useGetAllPlansQuery,
} from "../../generated/types";
import { Project } from "../../types";

type CopyTasksAndComponentsModalProps = {
  project: Project;
  // The plan the tasks and components will be copied into.
  targetPlanId: string;
  onCompleted?: () => void;
};

const COPY_SUCCESS = "Tasks and components copied!";
const COPY_ERROR = "Failed to copy tasks and components";

export const CopyTasksAndComponentsModal = (
  props: CopyTasksAndComponentsModalProps,
) => {
  const { project, targetPlanId, onCompleted } = props;
  const notify = useNotification();

  const [visible, setVisible] = useState(false);
  const [sourcePlanId, setSourcePlanId] = useState<string | undefined>();

  const { data: plansData, loading: plansLoading } = useGetAllPlansQuery({
    variables: { ...project },
    skip: !visible,
  });

  const selectablePlans = useMemo(
    () => (plansData?.plans ?? []).filter((plan) => plan.id !== targetPlanId),
    [plansData?.plans, targetPlanId],
  );

  const [copyTasksAndComponents, { loading: copying }] =
    useCopyTasksAndComponentsMutation({
      update: (cache) => {
        cache.evict({ fieldName: "tasksByParentPlan" });
        cache.evict({ fieldName: "plan" });
        cache.gc();
      },
      refetchQueries: ["GetTasksByParentPlan"],
      awaitRefetchQueries: true,
    });

  const resetState = () => {
    setSourcePlanId(undefined);
  };

  const onCancel = () => {
    setVisible(false);
    resetState();
  };

  const onCreate = async () => {
    if (!sourcePlanId) {
      return;
    }
    try {
      await copyTasksAndComponents({
        variables: {
          ...project,
          sourcePlanId,
          targetPlanId,
        },
      });
      notify(COPY_SUCCESS, "success");
      setVisible(false);
      resetState();
      onCompleted?.();
    } catch (error) {
      const errorMessage = (error as Error)?.message ?? COPY_ERROR;
      notify(errorMessage, "error");
    }
  };

  return (
    <>
      <Button type="primary" onClick={() => setVisible(true)}>
        Import Tasks
        <ImportOutlined />
      </Button>
      <Modal
        open={visible}
        title="Import Tasks from another plan"
        onCancel={onCancel}
        footer={[
          <Button key="cancel" onClick={onCancel}>
            Cancel
          </Button>,
          <Button
            key="create"
            type="primary"
            loading={copying}
            disabled={!sourcePlanId}
            onClick={onCreate}
          >
            Create
          </Button>,
        ]}
      >
        <Select
          style={{ width: "100%" }}
          placeholder="Select a plan to copy from..."
          loading={plansLoading}
          value={sourcePlanId}
          onChange={setSourcePlanId}
          options={selectablePlans.map((plan) => ({
            label: plan.name,
            value: plan.id,
          }))}
        />
      </Modal>
    </>
  );
};
